import * as signalR from '@microsoft/signalr';
import { create } from 'zustand';
import { getFreshAccessToken } from '@/lib/api';

const BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:5000/v1')
  .replace(/\/v1\/?$/, '');

export type HubNome = 'surgery' | 'notifications';

/**
 * - `inativa`: nunca conectou ou foi encerrada de propósito (logout, saída da tela).
 * - `reconectando`: caiu depois de conectada; o cliente está tentando voltar.
 * - `caiu`: não conseguiu conectar; nova tentativa agendada.
 */
export type EstadoConexao = 'inativa' | 'conectando' | 'conectada' | 'reconectando' | 'caiu';

type RealtimeState = {
  estados: Record<HubNome, EstadoConexao>;
  definir: (hub: HubNome, estado: EstadoConexao) => void;
};

/** Store e não hook: as conexões são módulos, criados fora de componentes. */
export const useRealtimeStore = create<RealtimeState>((set) => ({
  estados: { surgery: 'inativa', notifications: 'inativa' },
  definir: (hub, estado) => set((s) => ({ estados: { ...s.estados, [hub]: estado } })),
}));

const definir = (hub: HubNome, estado: EstadoConexao) => useRealtimeStore.getState().definir(hub, estado);

/** Espera antes da tentativa N (0, 1, 2…): 1 s, 2 s, 4 s… até 30 s. */
export const atrasoReconexaoMs = (tentativa: number) => Math.min(30_000, 1_000 * 2 ** tentativa);

type Meta = {
  hub: HubNome;
  aoReconectar: Set<() => void>;
  encerrada: boolean;
  /** Start em andamento: duas telas pedindo conexão ao mesmo tempo esperam o mesmo start. */
  inicio?: Promise<void>;
};
const metas = new WeakMap<signalR.HubConnection, Meta>();

function criarConexao(path: string, hub: HubNome): signalR.HubConnection {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE}${path}`, {
      // Chamado a cada (re)conexão: renova o token se estiver para vencer. Ler o token direto
      // da memória fazia a reconexão usar um JWT de 15 min já expirado.
      accessTokenFactory: () => getFreshAccessToken(),
    })
    // O padrão do SignalR desiste depois de 4 tentativas (~42 s). Numa cirurgia, uma queda de
    // rede de um minuto deixava a sala sem tempo real até o F5. Aqui não desiste: backoff
    // exponencial limitado a 30 s, e o aviso na tela diz que está tentando.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (ctx) => atrasoReconexaoMs(ctx.previousRetryCount),
    })
    .build();

  const meta: Meta = { hub, aoReconectar: new Set(), encerrada: false };
  metas.set(conn, meta);

  conn.onreconnecting(() => definir(hub, 'reconectando'));
  conn.onreconnected(() => {
    definir(hub, 'conectada');
    // O servidor esquece os grupos da conexão antiga: quem estava numa sala precisa entrar de
    // novo, e eventos do intervalo foram perdidos. Cada tela registra o que refazer.
    meta.aoReconectar.forEach((fn) => fn());
  });
  conn.onclose(() => {
    if (!meta.encerrada) definir(hub, 'caiu');
  });
  return conn;
}

/**
 * Registra o que refazer depois de uma reconexão (entrar de novo no grupo, revalidar dados).
 * Devolve a função de cancelamento — o `onreconnected` do SignalR não tem remoção, e registrar
 * direto nele a cada montagem de tela acumulava handlers.
 */
export function aoReconectar(conn: signalR.HubConnection, fn: () => void): () => void {
  const meta = metas.get(conn);
  if (!meta) return () => {};
  meta.aoReconectar.add(fn);
  return () => meta.aoReconectar.delete(fn);
}

let surgeryConn: signalR.HubConnection | null = null;

export function surgeryHub(): signalR.HubConnection {
  surgeryConn ??= criarConexao('/realtime/surgery', 'surgery');
  return surgeryConn;
}

let notificationConn: signalR.HubConnection | null = null;

/** Hub de notificações: o servidor põe a conexão no grupo do usuário ao conectar. */
export function notificationHub(): signalR.HubConnection {
  notificationConn ??= criarConexao('/realtime/notifications', 'notifications');
  return notificationConn;
}

/**
 * Encerra e descarta a conexão de notificações. Descartar (e não só parar) importa no logout:
 * o grupo é por usuário, e a próxima sessão precisa de uma conexão nova com o token dela.
 */
export async function stopNotificationHub(): Promise<void> {
  const conn = notificationConn;
  notificationConn = null;
  if (!conn) return;
  const meta = metas.get(conn);
  if (meta) meta.encerrada = true;
  definir('notifications', 'inativa');
  await conn.stop().catch(() => {});
}

export async function ensureConnected(conn: signalR.HubConnection): Promise<void> {
  if (conn.state === signalR.HubConnectionState.Connected) return;
  const meta = metas.get(conn);
  if (meta?.inicio) return meta.inicio;
  if (conn.state !== signalR.HubConnectionState.Disconnected) return;

  const inicio = (async () => {
    if (meta) {
      meta.encerrada = false;
      definir(meta.hub, 'conectando');
    }
    try {
      await conn.start();
      if (meta) definir(meta.hub, 'conectada');
    } catch (e) {
      if (meta) definir(meta.hub, 'caiu');
      throw e;
    }
  })();
  if (meta) meta.inicio = inicio;
  try {
    await inicio;
  } finally {
    if (meta) meta.inicio = undefined;
  }
}

/**
 * Conecta e, se a PRIMEIRA conexão falhar (API fora do ar no carregamento), continua tentando com
 * backoff. O `withAutomaticReconnect` só cobre quedas depois de conectado. Devolve o cancelamento.
 */
export function manterConectada(conn: signalR.HubConnection, aoConectar?: () => void): () => void {
  let cancelado = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tentar = async (tentativa: number) => {
    if (cancelado) return;
    try {
      await ensureConnected(conn);
      if (!cancelado) aoConectar?.();
    } catch {
      if (!cancelado) timer = setTimeout(() => void tentar(tentativa + 1), atrasoReconexaoMs(tentativa));
    }
  };
  void tentar(0);

  return () => {
    cancelado = true;
    if (timer) clearTimeout(timer);
  };
}
